import type * as RemixReact from "@remix-run/react";
import { ContractTypeParam } from "~/domain/contracts";
import { RequestTypeParam } from "~/domain/requestType";

export function home() {
  return absolute();
}

export function buyerGuides() {
  return absolute(["buyer-guides"]);
}

export function vendrVerified() {
  return absolute(["vendr-verified"]);
}

export function chromeExtensionMarketingPage() {
  return absolute(["software-prices"]);
}

export function marketplace() {
  return absolute(["marketplace"]);
}

export const buyers = {
  index: () => absolute(["buyers"]),
  products: () => absolute(["buyers", "products"]),
  product: ({ slug }: { slug: string }) =>
    absolute(["buyers", "products", slug]),
  following: () => absolute(["buyers", "following"]),
  followingAdd: () => absolute(["buyers", "following", "add"]),
  unfollow: () => absolute(["buyers", "following", "unfollow"]),
  track: () => absolute(["buyers", "track-products"]),
  contracts: () => absolute(["buyers", "contracts"]),

  // New routes
  batches: () => absolute(["buyers", "batches"]),
  batch: ({ id }: { id: string }) => absolute(["buyers", "batches", id]),
  colorScheme: () => absolute(["buyers", "color-scheme"]),
  download: ({ documentReferenceId }: { documentReferenceId: string }) =>
    absolute(["doc-assets", documentReferenceId]),
  group: ({ id }: { id: string }) => absolute(["buyers", "groups", id]),
  groups: () => absolute(["buyers", "groups"]),
  priceCheck: ({ id }: { id: string }) =>
    absolute(["buyers", "price-checks", id]),
  priceCheckSku: ({
    id,
    skuId,
    quantity,
  }: {
    id: string;
    skuId: string;
    quantity: number;
  }) => absolute(["buyers", "price-checks", id, "sku", skuId], { quantity }),
  skuSubscribe: ({ skuId }: { skuId: string }) =>
    absolute(["api", "skus", skuId, "subscribe"]),
};

export function partners() {
  return {
    index: () => absolute(["partners"]),
    actionLinks: () => absolute(["partners", "action-links"]),
    apiActionLinksMode: (actionLinkId: string) =>
      absolute(["partners", "api", "v1", "action-links", actionLinkId, "mode"]),
    viewActionLink: (actionLinkId: string) =>
      absolute(["partners", "action-links", actionLinkId]),
    getCreateActionLinkPath: () =>
      absolute(["partners", "action-links", "create"]),
    getEditActionLinkPath: (actionLinkId: string) =>
      absolute(["partners", "action-links", actionLinkId, "edit"]),
    login: () => absolute(["partners", "login"]),
    logout: ({ origin }: { origin: string }) => {
      const queryParams: Record<string, string | number | boolean> = {
        origin,
      };

      return absolute(["partners", "logout"], queryParams);
    },
  };
}

export function company(slug: string, productId?: string) {
  return absolute(
    ["marketplace", slug],
    productId ? { product: productId } : {},
  );
}

type NewRequestType = { slugOrId: string } & (
  | {
      requestType: RequestTypeParam;
      priceEstimateId?: string;
      contractType?: ContractTypeParam;
    }
  | {
      requestType: RequestTypeParam;
    }
);

export function newRequest(args: NewRequestType) {
  const queryParams: Record<string, string | number | boolean> = {
    requestType: args.requestType,
  };

  if ("priceEstimateId" in args && args.priceEstimateId) {
    queryParams.priceEstimateId = args.priceEstimateId;
  }

  if ("contractType" in args && args.contractType) {
    queryParams.contractType = args.contractType;
  }

  return absolute(["marketplace", args.slugOrId], queryParams);
}

export function buyerGuide(companyIdOrSlug: string) {
  return absolute(["buyer-guides", companyIdOrSlug], {});
}

export function startRequest({
  priceEstimateId,
  requestType,
  companySlugOrId,
  productId,
  haveRenewalQuote,
  typeOfPurchase,
  newPriceCheckFlow,
  returnPage,
}: {
  priceEstimateId?: string;
  requestType?: "priceEstimate" | "priceCheck" | "quote";
  companySlugOrId: string;
  productId?: string;
  haveRenewalQuote?: boolean;
  typeOfPurchase?: string;
  newPriceCheckFlow?: boolean;
  returnPage?: "buyer-guide" | "company";
}) {
  const params = {
    ...(requestType && { requestType }),
    ...(priceEstimateId && { priceEstimateId }),
    ...(returnPage && { returnPage }),
    // Initial answers
    ...(productId && { productId }),
    ...(haveRenewalQuote && { haveRenewalQuote }),
    ...(typeOfPurchase && { typeOfPurchase }),
    ...(newPriceCheckFlow && { newPriceCheckFlow }),
  };
  return absolute(["request", "new", companySlugOrId], params);
}

export function intelligence() {
  return absolute(["intelligence"]);
}

export function about() {
  return absolute(["about"]);
}

export function buyWithAws() {
  return absolute(["buy-with-aws"]);
}

export function priceChecks() {
  return absolute(["vendr-price-checks"]);
}

export function procurement() {
  return absolute(["procurement"]);
}

export function login() {
  return relative([], { login: true });
}

export function logout(options: { redirectBackTo?: RemixReact.Location } = {}) {
  const queryParams: Record<string, string | number | boolean> =
    options.redirectBackTo ? { origin: options.redirectBackTo.pathname } : {};

  return absolute(["logout"], queryParams);
}

export function categories() {
  return absolute(["categories"]);
}

export function category(slug: string) {
  return absolute(["categories", slug]);
}

export function subCategory(
  slug1: string,
  slug2: string,
  verifiedOnly: boolean = false,
  page: number = 1,
) {
  return absolute(["categories", slug1, slug2], {
    verified: verifiedOnly,
    page,
  });
}

export function connectApi() {
  return absolute(["connect-api"]);
}

export function myRequests(options?: { id?: string }) {
  const base = ["requests"];

  if (options?.id) {
    base.push(options.id);
  }

  return absolute(base);
}

export function priceEstimates(options: { id: string }) {
  return absolute(["marketplace", "price-estimates", options.id]);
}

export function saasReport() {
  return absolute(["insights", "saas-trends-report-2025"]);
}

export const api = {
  documents: {
    create: () => absolute(["api", "documents"]),
    update: () => absolute(["api", "documents"]),
  },
  pollPriceChecks: () => absolute(["api", "price-checks", "polls"]),
};

/**
 * Constructs a relative URL from the given path segments and query parameters.
 *
 * @param pathSegments - An array of strings representing the path segments to be joined.
 * @param queryParams - An object where keys and values represent the query parameters to be appended.
 * @returns A string representing the constructed URL.
 */
const relative = (
  pathSegments: string[] = [],
  queryParams: Record<string, string | number | boolean> = {},
): string => {
  const path = pathSegments
    .map((segment) => encodeURIComponent(segment))
    .join("/");

  const queryString = Object.entries(queryParams)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    )
    .join("&");

  const url = `${path}${queryString ? "?" + queryString : ""}`;

  return url;
};

const absolute = (
  pathSegments: string[] = [],
  queryParams: Record<string, string | number | boolean> = {},
): string => {
  return `/${relative(pathSegments, queryParams)}`;
};
